import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

import { FlowConditionList, FlowConditionSummary } from './flow-condition-list';
import { FlowEventCard } from './flow-event-card';
import { PAUSE_MESSAGE } from './flow-paused-notice';
import { FlowReorderColumn } from './flow-reorder-column';

import type { Block, Task } from '@/types/experience';
import type { FlowCondition, FlowRule } from '@agavi/blocks';

import {
  acknowledgeRule, flowCardKey, getCardProblem, getRuleChanges, getRuleContradictions,
  refreshConditionLabels,
} from '@/lib/flow-utils';

interface FlowRuleRowProps {
  rule: FlowRule;
  index: number;
  rulesLength: number;
  blocks: Block[];
  tasks: Task[];
  currentTaskId: string;
  /** Whether a student can arrive here twice — see `reenterableTasks`. */
  reenterable: boolean;
  /** This event's exit content, when it is an exit — see `FlowEventCard`. */
  exit?: { has: boolean; onOpen: () => void };
  onCreateDetourTask?: (names: { pathwayName: string; taskTitle: string }) => Promise<string | null>;
  /** See FlowEventCard: nothing follows this task, so "go" has to ask where. */
  noNextTask?: boolean;
  /** False only for the event just added — see `justAddedRuleId` in the panel. */
  defaultCollapsed?: boolean;
  /** Another event in the list says exactly the same thing. Flagged, never paused: both fire
   *  today (the first wins by ladder order), so stopping them would remove a working route. */
  /** An earlier event already catches these exact conditions, so this one can never fire. */
  shadowedBy?: string;
  onChange: (rule: FlowRule) => void;
  onDelete: () => void;
  /** Keyboard-operable alternative to drag-reorder — dnd-kit's sensors here are pointer/touch
   * only (no KeyboardSensor is wired up), so without these a keyboard user could never reorder
   * Special events at all, unlike the main block list which already has this fallback. */
  onMoveUp: () => void;
  onMoveDown: () => void;
  upRef?: (el: HTMLButtonElement | null) => void;
  downRef?: (el: HTMLButtonElement | null) => void;
}

export default function FlowRuleRow({ rule, index, rulesLength, blocks, tasks, currentTaskId, reenterable, exit, onCreateDetourTask, noNextTask, defaultCollapsed, shadowedBy, onChange, onDelete, onMoveUp, onMoveDown, upRef, downRef }: FlowRuleRowProps) {
  const { t, i18n } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id });
  /**
   * One state. A card runs, or it is paused until fixed -- no separate `paused` boolean beside a
   * `problem` string beside a severity, which was three descriptions of one fact.
   *
   * `settled` is always true for a built-in or saved event: these are not freshly dropped drafts
   * the way a new Special event is, and the collapse state is the card's own. A Special event
   * added seconds ago opens expanded and reports nothing until it is collapsed or blurred.
   */
  const own = getCardProblem(
    rule, blocks, defaultCollapsed !== false || rule.conditions.length > 0,
    { action: rule.action }, tasks,
  );
  /** Its own faults first: being shadowed matters less than being broken, and a card with both
   *  should be told about the one it can actually act on. */
  const problem = own ?? (shadowedBy
    ? { kind: 'unreachable' as const, conditionKeys: new Set<string>(), acceptable: false }
    : null);
  const paused = problem !== null;
  const changes = problem?.kind === 'drifted' ? getRuleChanges(rule, blocks, t, i18n.language) : undefined;
  const contradictions = problem?.kind === 'broken' ? getRuleContradictions(rule, blocks, t, i18n.language) : undefined;
  // Only drift can be blessed as intentional; a broken or unfinished card has to be edited.
  const canAccept = problem?.acceptable === true;

  /** Every edit to the list goes back through refreshConditionLabels, which is where a newly added
   * condition picks up its label and acknowledgement snapshot — without it a fresh condition has no
   * signature on record and could never be detected as drifting later. */
  function setConditions(conditions: FlowCondition[]) {
    onChange(refreshConditionLabels({ ...rule, conditions }, blocks, t, i18n.language));
  }

  return (
    /* `data-flow-card` is how an arrival from the board's problems panel finds this row: a routing
       fault renders nowhere but here, and here it renders collapsed in a list of a dozen. */
    <div ref={setNodeRef} data-flow-card={flowCardKey('rule', rule.id)}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1 ${isDragging ? 'opacity-40' : ''}`}>
      <FlowReorderColumn
        index={index}
        total={rulesLength}
        dragLabel={t('task.flow.rule.dragLabel', { index: index + 1 })}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        upRef={upRef}
        downRef={downRef}
        listeners={listeners}
        attributes={attributes}
      />
      <div className='min-w-0 flex-1'>
        <FlowEventCard
          title={rule.title}
          onTitleChange={(title) => onChange({ ...rule, title })}
          matchMode={rule.matchMode}
          onMatchModeChange={(matchMode) => onChange({ ...rule, matchMode })}
          action={rule.action}
          onActionChange={(action) => onChange({ ...rule, action })}
          category='gate'
          noNextTask={noNextTask}
          special
          defaultCollapsed={defaultCollapsed}
          tasks={tasks}
          currentTaskId={currentTaskId}
          onCreateDetourTask={onCreateDetourTask}
          // One problem concept with a severity, rather than a red `problem` and an amber `paused`
          // answering the same question in two voices. An event with nothing to match on never
          // fires — the engine already ignores it, and without this the teacher's only clue was a
          // card that looked finished and did nothing.
          problem={problem
            ? t(PAUSE_MESSAGE[problem.kind], {
                title: problem.kind === 'targetDeleted'
                  ? (rule.action.type === 'goto' ? rule.action.targetTitle : undefined)
                    ?? t('task.flow.rule.pausedTargetUnnamed')
                  : shadowedBy ?? '',
              })
            : undefined}
          severity={problem ? 'warning' : undefined}
          paused={paused}
          changes={changes}
          contradictions={contradictions}
          onAccept={canAccept ? () => onChange(acknowledgeRule(rule, blocks)) : undefined}
          onDelete={onDelete}
          exit={exit}
          summary={<FlowConditionSummary conditions={rule.conditions} blocks={blocks} tasks={tasks} matchMode={rule.matchMode} />}
          conditions={
            /* One state, one tone: `problemSeverity` is always `warning` now. Red for "can never
               fire" beside amber for "meaning moved" drew a distinction a teacher cannot act on
               differently -- both say go and fix it, and the sentence above says which. */
            <FlowConditionList
              tasks={tasks}
              currentTaskId={currentTaskId}
              reenterable={reenterable}
              conditions={rule.conditions}
              blocks={blocks}
              matchMode={rule.matchMode}
              problemKeys={problem?.conditionKeys}
              problemSeverity={problem ? 'warning' : undefined}
              onChange={setConditions}
              conditionLabels={rule.conditionLabels}
            />
          }
        />
      </div>
    </div>
  );
}
